import { FC, useEffect, useState } from "react";
import {
  HandleLogOut,
  HandleReturn,
  HandleCrumbSelect,
  StopEventPropagation,
  UpdateUserLastActionDate,
} from "../../helper/ui/methods";
import { useAppStateContext } from "../../middleware/context/state-context-provider";
import { Navigate } from "react-router-dom";
import MainNavigationPanel from "../ui/user/main-navigation-panel";
import MainFooterPanel from "../ui/user/main-footer-panel";
import ReportContent from "../ui/report/report-content";
import MenuButton from "../ui/user/menu-button";
import "../../styles/report-viewer.css";
import ReportContentFilterDialog from "../ui/report/report-content-filter-dialog";
import { ReportElementInterface } from "../../middleware/interfaces/report/report-element-interface";
import ReportElementNotePanel from "../ui/report/report-element-note-panel";
import ReportElementNoteDialog from "../ui/report/report-element-note-dialog";
import ResponseDialog from "../ui/user/response-dialog";
import { ReportElementNoteInterface } from "../../middleware/interfaces/report/report-element-note-interface";
import ReportElementNoteEditDialog from "../ui/report/report-element-note-edit-dialog";
import ReportContentSettingDialog from "../ui/report/report-content-setting-dialog";
import CanvasHeader from "../ui/user/canvas-header";
import { CountVisibleReportElements } from "../../helper/report/methods";

export const ReportViewer: FC = () => {
  const [state, dispatch] = useAppStateContext();
  const [reportMaximize, setReportMaximize] = useState(false);
  const [
    reportContentSettingDialogReveal,
    setReportContentSettingDialogReveal,
  ] = useState(false);
  const [reportContentFilterDialogReveal, setReportContentFilterDialogReveal] =
    useState(false);
  const [userInputColumnCount, setUserInputColumnCount] = useState(2);
  const [selectedReportElement, setSelectedReportElement] =
    useState<ReportElementInterface | null>(null);
  const [selectedReportElementNote, setSelectedReportElementNote] =
    useState<ReportElementNoteInterface | null>(null);
  const [reportElementNoteDialogReveal, setReportElementNoteDialogReveal] =
    useState(false);

  const HandleReportDoubleClick = (event: React.MouseEvent) => {
    StopEventPropagation(event);
    setReportMaximize(!reportMaximize);
  };

  const HandleReportClick = () => {
    setSelectedReportElement(null);
  };

  const SetReportElementDataStyle = () => {
    if (reportMaximize) {
      return {
        "--ContentWidth": "100vw",
        "--ContentHeight": "100vh",
      } as React.CSSProperties;
    } else {
      return {
        "--ContentWidth": "60vw",
        "--ContentHeight": "calc(95vh - 2 * var(--NavAndFooterHeight))",
      } as React.CSSProperties;
    }
  };

  useEffect(() => {
    if (state.user) {
      UpdateUserLastActionDate(state.user, dispatch);
    }
  }, [state.user]);

  const visibleElementCount = state.openReport
    ? CountVisibleReportElements(state.openReport)
    : 0;

  return (
    <>
      {(() => {
        if (
          state.user?.openReport &&
          String(state.openReport?.reportID) === String(state.user.openReport)
        ) {
          return (
            <div
              id="pageWrapper"
              onClick={() => UpdateUserLastActionDate(state.user!, dispatch)}
            >
              {!reportMaximize && <MainNavigationPanel user={state.user} />}
              <div id="contentWrapper">
                {!reportMaximize && (
                  <div id="leftMenu">
                    <div className="menuButtonWrapper">
                      <MenuButton
                        imageSource="assets/icons/setting.png"
                        alternativeText="SETTING"
                        dispatch={() => {
                          setReportContentSettingDialogReveal(true);
                        }}
                      />
                      <MenuButton
                        imageSource="assets/icons/filter.png"
                        alternativeText="FILTER"
                        dispatch={() => {
                          setReportContentFilterDialogReveal(true);
                        }}
                      />
                      <MenuButton
                        imageSource="assets/icons/return.png"
                        alternativeText="RETURN"
                        dispatch={() => {
                          HandleReturn(state, dispatch);
                        }}
                      />
                      <MenuButton
                        imageSource="assets/icons/logout.png"
                        alternativeText="LOG OUT"
                        dispatch={() => {
                          HandleLogOut(dispatch);
                        }}
                      />
                    </div>
                  </div>
                )}
                <div
                  id="content"
                  className="canvasSurface"
                  onDoubleClick={HandleReportDoubleClick}
                  onClick={HandleReportClick}
                  style={SetReportElementDataStyle()}
                >
                  <CanvasHeader
                    crumbs={[
                      "PROJECTS",
                      state.user.openProject!.projectName,
                      state.openReport!.reportName,
                    ]}
                    onCrumbSelect={(crumbIndex) => {
                      HandleCrumbSelect(state, crumbIndex, dispatch);
                    }}
                    itemCount={visibleElementCount}
                    itemNoun="element"
                    selectedCount={selectedReportElement ? 1 : 0}
                  />
                  <ReportContent
                    user={state.user}
                    report={state.openReport!}
                    userInputColumnCount={userInputColumnCount}
                    selectedReportElement={selectedReportElement}
                    setSelectedReportElement={setSelectedReportElement}
                    setReportElementNoteDialogReveal={
                      setReportElementNoteDialogReveal
                    }
                    dispatch={dispatch}
                  />
                  <ReportContentSettingDialog
                    reveal={reportContentSettingDialogReveal}
                    userInputColumnCount={userInputColumnCount}
                    setReportContentSettingDialogReveal={
                      setReportContentSettingDialogReveal
                    }
                    setUserInputColumnCount={setUserInputColumnCount}
                  />
                  <ReportContentFilterDialog
                    reveal={reportContentFilterDialogReveal}
                    state={state}
                    setReportContentFilterDialogReveal={
                      setReportContentFilterDialogReveal
                    }
                    dispatch={dispatch}
                  />
                  <ReportElementNoteDialog
                    user={state.user}
                    reportElementNoteDialogReveal={
                      reportElementNoteDialogReveal
                    }
                    selectedReportElement={selectedReportElement}
                    setReportElementNoteDialogReveal={
                      setReportElementNoteDialogReveal
                    }
                    dispatch={dispatch}
                  />
                  <ReportElementNoteEditDialog
                    user={state.user}
                    selectedReportElementNote={selectedReportElementNote}
                    setSelectedReportElementNote={setSelectedReportElementNote}
                    dispatch={dispatch}
                  />
                  <ResponseDialog
                    user={state.user}
                    title="Server Response"
                    message={state.response}
                    dispatch={dispatch}
                  />
                </div>
                {!reportMaximize && (
                  <div id="rightMenu">
                    <ReportElementNotePanel
                      reportElement={selectedReportElement}
                      setSelectedReportElement={setSelectedReportElement}
                      setSelectedReportElementNote={
                        setSelectedReportElementNote
                      }
                    />
                  </div>
                )}
              </div>
              {!reportMaximize && <MainFooterPanel />}
            </div>
          );
        } else if (state.user) {
          // The report is on its way from the server (Back, Forward or a
          // refresh landed here), or it was just closed and LocationHandler
          // is taking the address back to the project.
          return (
            <div id="pageWrapper">
              <MainNavigationPanel user={state.user} />
              <div id="contentWrapper">
                <div id="content" className="canvasSurface">
                  <ResponseDialog
                    user={state.user}
                    title="Server Response"
                    message={state.response}
                    dispatch={dispatch}
                  />
                </div>
              </div>
              <MainFooterPanel />
            </div>
          );
        } else {
          return <Navigate to={"/authentication&registration"} replace />;
        }
      })()}
    </>
  );
};
